<script setup>
import { reactive, computed, watch, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
  initialDraft: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const inboxBehaviorCopy = {
  title: 'Enviar para a caixa somente ao responder',
  description:
    'As mensagens da campanha ficam salvas no histórico, mas a conversa só entra na caixa de entrada quando o cliente responder.',
};

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppCampaignInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
};

const toLocalDateTimeInput = timestamp => {
  if (!timestamp) return null;

  const date = new Date(Number(timestamp) * 1000);
  if (Number.isNaN(date.getTime())) return null;

  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
};

const getInitialState = (campaign, initialDraft = null) => {
  const triggerRules = campaign?.trigger_rules || {};
  if (!campaign) {
    return {
      title: initialDraft?.title || '',
      inboxId: null,
      templateId: null,
      scheduledAt: null,
      sendConnectionType: 'whatsapp',
      randomInterval: true,
      intervalValue: 2,
      intervalUnit: 'minutes',
      intervalMaxValue: 7,
      intervalMaxUnit: 'minutes',
      restrictDays: true,
      allowedDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      restrictHours: true,
      startTime: '08:00',
      endTime: '18:00',
      deadlineAt: null,
      audienceMode: initialDraft?.numbers?.length ? 'paste' : 'labels',
      selectedAudience: [],
      pastedNumbers: initialDraft?.numbers?.join('\n') || '',
      fileNumbers: [],
      selectedFileName: '',
      skipBlacklistedContacts: true,
      openConversationOnReplyOnly: true,
    };
  }

  const audience = campaign.audience || [];
  const phoneAudience = audience.filter(item => item.type === 'PhoneNumber');
  const labelAudience = audience.filter(item => item.type === 'Label');
  let audienceMode = 'labels';
  if (phoneAudience.length) {
    audienceMode = phoneAudience[0]?.source === 'file' ? 'file' : 'paste';
  }
  const phoneNumbers = phoneAudience.map(item => item.value).filter(Boolean);

  return {
    title: campaign.title || '',
    inboxId: campaign.inbox?.id || null,
    templateId: null,
    scheduledAt: toLocalDateTimeInput(campaign.scheduled_at),
    sendConnectionType: triggerRules.send_connection_type || 'whatsapp',
    randomInterval: !!triggerRules.random_interval,
    intervalValue: triggerRules.interval?.value || 2,
    intervalUnit: triggerRules.interval?.unit || 'minutes',
    intervalMaxValue: triggerRules.interval?.max_value || 7,
    intervalMaxUnit: triggerRules.interval?.max_unit || 'minutes',
    restrictDays: !!triggerRules.restrict_days,
    allowedDays: triggerRules.allowed_days || [],
    restrictHours: !!triggerRules.restrict_hours,
    startTime: triggerRules.send_window?.start_time || '08:00',
    endTime: triggerRules.send_window?.end_time || '18:00',
    deadlineAt: toLocalDateTimeInput(triggerRules.deadline_at),
    audienceMode,
    selectedAudience: labelAudience.map(item => item.id).filter(Boolean),
    pastedNumbers: phoneNumbers.join(', '),
    fileNumbers: phoneNumbers,
    selectedFileName: phoneNumbers.length ? 'Números importados' : '',
    skipBlacklistedContacts: triggerRules.skip_blacklisted_contacts !== false,
    openConversationOnReplyOnly:
      triggerRules.open_conversation_on_reply_only !== false,
  };
};

const state = reactive(getInitialState(props.campaign, props.initialDraft));
const templateParserRef = ref(null);
const formContentRef = ref(null);
const currentStep = ref(1);

const campaignSystemVariables = [
  {
    label: 'Nome do contato',
    value: '{{ contact.name }}',
    hint: '{{ contact.name }}',
  },
  {
    label: 'Primeiro nome',
    value: '{{ contact.first_name }}',
    hint: '{{ contact.first_name }}',
  },
  {
    label: 'Sobrenome',
    value: '{{ contact.last_name }}',
    hint: '{{ contact.last_name }}',
  },
  {
    label: 'Telefone do contato',
    value: '{{ contact.phone_number }}',
    hint: '{{ contact.phone_number }}',
  },
  {
    label: 'Email do contato',
    value: '{{ contact.email }}',
    hint: '{{ contact.email }}',
  },
  {
    key: 'nome_agente',
    label: 'Nome do agente',
    value: '{{ agent.name }}',
    hint: '{{ agent.name }}',
  },
  {
    label: 'Canal de atendimento',
    value: '{{ inbox.name }}',
    hint: '{{ inbox.name }}',
  },
  {
    label: 'Nome da empresa',
    value: '{{ account.name }}',
    hint: '{{ account.name }}',
  },
];
const isApplyingInitialState = ref(false);

const isEditing = computed(() => !!props.campaign?.id);
const isLastStep = computed(() => currentStep.value === 3);
const wizardSteps = computed(() => [
  {
    id: 1,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.WIZARD.INBOX'),
  },
  {
    id: 2,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.WIZARD.SETTINGS_TEMPLATE'),
  },
  {
    id: 3,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.WIZARD.AUDIENCE_SCHEDULE'),
  },
]);
const isSubmitting = computed(
  () => formState.uiFlags.value.isCreating || formState.uiFlags.value.isUpdating
);

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const audienceModeOptions = computed(() => [
  {
    value: 'labels',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.MODE_LABELS'),
    icon: 'i-lucide-tags',
  },
  {
    value: 'paste',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.MODE_PASTE'),
    icon: 'i-lucide-clipboard-list',
  },
  {
    value: 'file',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.MODE_FILE'),
    icon: 'i-lucide-file-up',
  },
]);

const connectionTypeOptions = computed(() => [
  {
    value: 'whatsapp',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.CONNECTION.WHATSAPP'),
    icon: 'i-ri-whatsapp-fill',
  },
  {
    value: 'api',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.CONNECTION.API'),
    icon: 'i-lucide-braces',
  },
]);

const intervalUnitOptions = computed(() => [
  {
    value: 'seconds',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.SECONDS'),
  },
  {
    value: 'minutes',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.MINUTES'),
  },
  {
    value: 'hours',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.HOURS'),
  },
]);

const dayOptions = computed(() => [
  {
    value: 'monday',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.MONDAY'),
  },
  {
    value: 'tuesday',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.TUESDAY'),
  },
  {
    value: 'wednesday',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.WEDNESDAY'),
  },
  {
    value: 'thursday',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.THURSDAY'),
  },
  {
    value: 'friday',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.FRIDAY'),
  },
  {
    value: 'saturday',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.SATURDAY'),
  },
  {
    value: 'sunday',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.SUNDAY'),
  },
]);

const inboxOptions = computed(
  () =>
    formState.inboxes.value
      ?.filter(inbox => {
        if (state.sendConnectionType === 'api') {
          return inbox.channel_type === 'Channel::Api';
        }

        return inbox.channel_type === 'Channel::Whatsapp';
      })
      .map(inbox => ({
        value: inbox.id,
        label: inbox.name,
      })) ?? []
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    // Create a more user-friendly label from template name
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const findTemplateIdForCampaign = () => {
  const templateName = props.campaign?.template_params?.name;
  if (!templateName) return null;

  return (
    templateOptions.value.find(option => option.template?.name === templateName)
      ?.value || null
  );
};

const normalizePhoneNumber = value => {
  const rawValue = value?.replace(/\s+/g, '').trim() || '';
  const digits = rawValue.replace(/\D/g, '');
  if (!digits) return '';

  if (rawValue.startsWith('+')) return `+${digits}`;
  if (digits.startsWith('55')) return `+${digits}`;
  return `+55${digits}`;
};

const parsePhoneNumbers = value => {
  return [
    ...new Set(
      value
        .split(/[\n\r,;]+/)
        .map(normalizePhoneNumber)
        .filter(phoneNumber => /^\+[1-9]\d{9,14}$/.test(phoneNumber))
    ),
  ].slice(0, 10000);
};

const pastedPhoneNumbers = computed(() =>
  parsePhoneNumbers(state.pastedNumbers)
);

const parsedAudienceNumbers = computed(() => {
  if (state.audienceMode === 'paste') return pastedPhoneNumbers.value;
  if (state.audienceMode === 'file') return state.fileNumbers;
  return [];
});

const audienceCount = computed(() => {
  return state.audienceMode === 'labels'
    ? state.selectedAudience.length
    : parsedAudienceNumbers.value.length;
});

const hasSelectedAudience = value => {
  if (state.audienceMode === 'labels') return value.length > 0;
  return parsedAudienceNumbers.value.length > 0;
};

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  templateId: { required },
  scheduledAt: {
    required,
    validDateTime: value => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value),
  },
  selectedAudience: { hasSelectedAudience },
};

const v$ = useVuelidate(rules, state);

const formErrors = computed(() => ({
  title: v$.value.title.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.ERROR')
    : '',
  inbox: v$.value.inboxId.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.ERROR')
    : '',
  template: v$.value.templateId.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.ERROR')
    : '',
  scheduledAt: v$.value.scheduledAt.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.ERROR')
    : '',
  audience: v$.value.selectedAudience.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.ERROR')
    : '',
}));

const hasRequiredTemplateParams = computed(() => {
  return templateParserRef.value?.v$?.$invalid === false;
});

const isSubmitDisabled = computed(
  () => v$.value.$invalid || !hasRequiredTemplateParams.value
);

const formatToUTCString = localDateTime => {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(localDateTime || '')) {
    return null;
  }

  const date = new Date(localDateTime);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const handleCancel = () => emit('cancel');

const validateCurrentStep = async () => {
  if (currentStep.value === 1) {
    const [isTitleValid, isInboxValid] = await Promise.all([
      v$.value.title.$validate(),
      v$.value.inboxId.$validate(),
    ]);
    return isTitleValid && isInboxValid;
  }

  if (currentStep.value === 2) {
    const isTemplateValid = await v$.value.templateId.$validate();
    if (!isTemplateValid) return false;

    await nextTick();
    templateParserRef.value?.v$?.$touch();
    return hasRequiredTemplateParams.value;
  }

  return true;
};

const scrollCurrentStepToTop = () => {
  nextTick(() => {
    if (formContentRef.value) formContentRef.value.scrollTop = 0;
  });
};

const handleNext = async () => {
  if (!(await validateCurrentStep())) return;
  currentStep.value = Math.min(3, currentStep.value + 1);
  scrollCurrentStepToTop();
};

const handlePrevious = () => {
  if (currentStep.value === 1) {
    handleCancel();
    return;
  }

  currentStep.value -= 1;
  scrollCurrentStepToTop();
};

const goToStep = step => {
  if (step >= currentStep.value) return;
  currentStep.value = step;
  scrollCurrentStepToTop();
};

const toggleAllowedDay = day => {
  if (state.allowedDays.includes(day)) {
    state.allowedDays = state.allowedDays.filter(item => item !== day);
    return;
  }

  state.allowedDays = [...state.allowedDays, day];
};

const handleFileUpload = async event => {
  const [file] = event.target.files || [];
  if (!file) return;

  state.selectedFileName = file.name;
  const content = await file.text();
  state.fileNumbers = parsePhoneNumbers(content);
};

const prepareCampaignDetails = () => {
  // Find the selected template to get its content
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;

  // Extract template content - this should be the template message body
  const templateContent = parserData?.renderedTemplate || '';

  // Prepare template_params object with the same structure as used in contacts
  const templateParams = {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    parameter_format: currentTemplate?.parameter_format || 'POSITIONAL',
    components: currentTemplate?.components || [],
    processed_params: parserData?.processedParams || {},
  };

  const labelAudience = state.selectedAudience?.map(id => ({
    id,
    type: 'Label',
  }));
  const phoneAudience = parsedAudienceNumbers.value.map(phoneNumber => ({
    type: 'PhoneNumber',
    value: phoneNumber,
    source: state.audienceMode,
  }));

  return {
    title: state.title,
    message: templateContent,
    template_params: templateParams,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: state.audienceMode === 'labels' ? labelAudience : phoneAudience,
    trigger_rules: {
      send_connection_type: state.sendConnectionType,
      random_interval: state.randomInterval,
      interval: {
        value: Number(state.intervalValue) || 0,
        unit: state.intervalUnit,
        max_value: state.randomInterval
          ? Number(state.intervalMaxValue) || 0
          : null,
        max_unit: state.randomInterval ? state.intervalMaxUnit : null,
      },
      restrict_days: state.restrictDays,
      allowed_days: state.restrictDays ? state.allowedDays : [],
      restrict_hours: state.restrictHours,
      send_window: state.restrictHours
        ? {
            start_time: state.startTime,
            end_time: state.endTime,
          }
        : null,
      deadline_at: formatToUTCString(state.deadlineAt),
      skip_blacklisted_contacts: state.skipBlacklistedContacts,
      open_conversation_on_reply_only: state.openConversationOnReplyOnly,
    },
  };
};

const handleSubmit = async () => {
  if (!isLastStep.value) {
    await handleNext();
    return;
  }

  templateParserRef.value?.v$?.$touch();
  if (!hasRequiredTemplateParams.value) return;

  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
};

// Reset template selection when inbox changes
watch(
  () => state.inboxId,
  (newValue, oldValue) => {
    if (isApplyingInitialState.value || newValue === oldValue) return;
    state.templateId = null;
  }
);

watch(
  () => state.sendConnectionType,
  () => {
    state.inboxId = null;
    state.templateId = null;
  }
);

watch(
  () => [props.campaign, props.initialDraft],
  async ([campaign, initialDraft]) => {
    isApplyingInitialState.value = true;
    currentStep.value = 1;
    Object.assign(state, getInitialState(campaign, initialDraft));
    v$.value.$reset();
    await nextTick();
    state.templateId = findTemplateIdForCampaign();
    isApplyingInitialState.value = false;
  },
  { immediate: true }
);

watch(templateOptions, () => {
  if (!isEditing.value || state.templateId) return;
  state.templateId = findTemplateIdForCampaign();
});
</script>

<template>
  <form class="flex min-h-0 flex-1 flex-col" @submit.prevent="handleSubmit">
    <nav
      class="flex-shrink-0 border-b border-n-weak px-6 py-5"
      :aria-label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.WIZARD.ARIA_LABEL')"
    >
      <ol class="m-0 flex list-none p-0">
        <li
          v-for="step in wizardSteps"
          :key="step.id"
          class="relative flex min-w-0 flex-1 justify-center"
        >
          <span
            v-if="step.id < wizardSteps.length"
            class="absolute left-[calc(50%+1.25rem)] right-[calc(-50%+1.25rem)] top-4 h-px"
            :class="step.id < currentStep ? 'bg-n-teal-8' : 'bg-n-weak'"
          />
          <button
            type="button"
            class="relative z-10 flex min-w-0 flex-col items-center gap-2 border-0 bg-transparent px-2 text-center"
            :class="step.id < currentStep ? 'cursor-pointer' : 'cursor-default'"
            :disabled="step.id >= currentStep"
            :aria-current="step.id === currentStep ? 'step' : undefined"
            @click="goToStep(step.id)"
          >
            <span
              class="flex size-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-colors"
              :class="
                step.id === currentStep
                  ? 'border-n-brand bg-n-brand text-white ring-4 ring-n-brand/10'
                  : step.id < currentStep
                    ? 'border-n-teal-7 bg-n-teal-3 text-n-teal-11'
                    : 'border-n-weak bg-n-solid-2 text-n-slate-10'
              "
            >
              <span
                v-if="step.id < currentStep"
                class="i-lucide-check size-4"
              />
              <span v-else>{{ step.id }}</span>
            </span>
            <span
              class="max-w-40 text-xs font-medium leading-4 sm:text-sm"
              :class="
                step.id === currentStep
                  ? 'text-n-brand'
                  : step.id < currentStep
                    ? 'text-n-slate-12'
                    : 'text-n-slate-10'
              "
            >
              {{ step.label }}
            </span>
          </button>
        </li>
      </ol>
    </nav>

    <div
      ref="formContentRef"
      class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5"
    >
      <div v-show="currentStep === 1" class="flex flex-col gap-4">
        <Input
          v-model="state.title"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
          :message="formErrors.title"
          :message-type="formErrors.title ? 'error' : 'info'"
        />

        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CONNECTION.LABEL') }}
          </label>
          <div class="grid grid-cols-2 gap-2">
            <Button
              v-for="option in connectionTypeOptions"
              :key="option.value"
              type="button"
              :label="option.label"
              :icon="option.icon"
              :variant="
                state.sendConnectionType === option.value ? 'faded' : 'outline'
              "
              :color="
                state.sendConnectionType === option.value ? 'blue' : 'slate'
              "
              class="w-full"
              @click="state.sendConnectionType = option.value"
            />
          </div>
        </div>

        <div
          class="flex gap-3 rounded-lg border border-n-amber-7 bg-n-amber-3 p-3 text-sm text-n-amber-12"
        >
          <span class="i-lucide-triangle-alert mt-0.5 size-5 flex-shrink-0" />
          <div class="min-w-0">
            <p class="mb-1 font-semibold">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.BAN_WARNING.TITLE') }}
            </p>
            <p class="mb-0 text-xs leading-5 text-n-amber-11">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.BAN_WARNING.DESCRIPTION') }}
            </p>
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
          </label>
          <ComboBox
            id="inbox"
            v-model="state.inboxId"
            :options="inboxOptions"
            :has-error="!!formErrors.inbox"
            :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
            :message="formErrors.inbox"
            teleport-to-body
          />
        </div>
      </div>

      <div v-show="currentStep === 2" class="flex flex-col gap-4">
        <section
          class="flex flex-col gap-4 rounded-lg border border-n-weak bg-n-alpha-black2 p-4"
        >
          <div class="flex items-center justify-between gap-3">
            <div>
              <h4 class="mb-1 text-sm font-semibold text-n-slate-12">
                {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.TITLE') }}
              </h4>
              <p class="mb-0 text-xs text-n-slate-11">
                {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.SUBTITLE') }}
              </p>
            </div>
          </div>

          <div class="flex flex-col gap-3 border-t border-n-weak pt-4">
            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <Checkbox v-model="state.randomInterval" />
              {{
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.RANDOM_INTERVAL')
              }}
            </label>

            <div
              class="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto_1fr_1fr]"
            >
              <Input
                v-model="state.intervalValue"
                type="number"
                min="1"
                :label="
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.INTERVAL')
                "
              />
              <div class="flex flex-col gap-1">
                <label class="mb-0.5 text-sm font-medium text-n-slate-12">
                  {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.UNIT') }}
                </label>
                <ComboBox
                  v-model="state.intervalUnit"
                  :options="intervalUnitOptions"
                />
              </div>
              <span
                v-if="state.randomInterval"
                class="hidden self-end pb-2 text-sm text-n-slate-11 sm:block"
              >
                {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.UNTIL') }}
              </span>
              <Input
                v-if="state.randomInterval"
                v-model="state.intervalMaxValue"
                type="number"
                min="1"
                :label="
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.MAX_INTERVAL')
                "
              />
              <div v-if="state.randomInterval" class="flex flex-col gap-1">
                <label class="mb-0.5 text-sm font-medium text-n-slate-12">
                  {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.UNIT') }}
                </label>
                <ComboBox
                  v-model="state.intervalMaxUnit"
                  :options="intervalUnitOptions"
                />
              </div>
            </div>
          </div>

          <div class="grid gap-4 border-t border-n-weak pt-4 sm:grid-cols-2">
            <div class="flex flex-col gap-3">
              <label class="flex items-center gap-2 text-sm text-n-slate-12">
                <Checkbox v-model="state.restrictDays" />
                {{
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.RESTRICT_DAYS')
                }}
              </label>
              <div v-if="state.restrictDays" class="flex flex-wrap gap-2">
                <Button
                  v-for="day in dayOptions"
                  :key="day.value"
                  type="button"
                  size="sm"
                  :label="day.label"
                  :variant="
                    state.allowedDays.includes(day.value) ? 'faded' : 'outline'
                  "
                  :color="
                    state.allowedDays.includes(day.value) ? 'blue' : 'slate'
                  "
                  @click="toggleAllowedDay(day.value)"
                />
              </div>
            </div>

            <div class="flex flex-col gap-3">
              <label class="flex items-center gap-2 text-sm text-n-slate-12">
                <Checkbox v-model="state.restrictHours" />
                {{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.RESTRICT_HOURS'
                  )
                }}
              </label>
              <div v-if="state.restrictHours" class="grid grid-cols-2 gap-2">
                <Input
                  v-model="state.startTime"
                  type="time"
                  :label="
                    t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.START_TIME')
                  "
                />
                <Input
                  v-model="state.endTime"
                  type="time"
                  :label="
                    t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.END_TIME')
                  "
                />
              </div>
            </div>
          </div>

          <Input
            v-model="state.deadlineAt"
            type="datetime-local"
            :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.DEADLINE')"
            :min="currentDateTime"
            :placeholder="
              t(
                'CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SETTINGS.DEADLINE_PLACEHOLDER'
              )
            "
          />
        </section>

        <div class="flex flex-col gap-1">
          <label
            for="template"
            class="mb-0.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
          </label>
          <ComboBox
            id="template"
            v-model="state.templateId"
            :options="templateOptions"
            :has-error="!!formErrors.template"
            :placeholder="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')
            "
            :message="formErrors.template"
            teleport-to-body
          />
          <p class="mt-1 text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
          </p>
        </div>

        <!-- Template Parser -->
        <WhatsAppTemplateParser
          v-if="selectedTemplate"
          ref="templateParserRef"
          :template="selectedTemplate"
          :system-variables="campaignSystemVariables"
        />
      </div>

      <div v-show="currentStep === 3" class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <label
            for="audience"
            class="mb-0.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
          </label>
          <div class="grid grid-cols-3 gap-2">
            <Button
              v-for="option in audienceModeOptions"
              :key="option.value"
              type="button"
              :label="option.label"
              :icon="option.icon"
              :variant="
                state.audienceMode === option.value ? 'faded' : 'outline'
              "
              :color="state.audienceMode === option.value ? 'blue' : 'slate'"
              class="w-full"
              @click="state.audienceMode = option.value"
            />
          </div>

          <TagMultiSelectComboBox
            v-if="state.audienceMode === 'labels'"
            v-model="state.selectedAudience"
            :options="audienceList"
            :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
            :placeholder="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')
            "
            :has-error="!!formErrors.audience"
            :message="formErrors.audience"
          />

          <TextArea
            v-else-if="state.audienceMode === 'paste'"
            v-model="state.pastedNumbers"
            :placeholder="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PASTE_PLACEHOLDER')
            "
            :message="formErrors.audience"
            :message-type="formErrors.audience ? 'error' : 'info'"
            :max-length="160000"
            auto-height
            resize
            min-height="7rem"
            max-height="14rem"
          />

          <div v-else class="flex flex-col gap-2">
            <label
              class="flex min-h-28 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-n-weak bg-n-alpha-black2 px-4 py-5 text-center hover:bg-n-alpha-2"
            >
              <span class="i-lucide-file-up size-5 text-n-slate-11" />
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.FILE_LABEL') }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{
                  state.selectedFileName ||
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.FILE_HELP')
                }}
              </span>
              <input
                class="hidden"
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                @change="handleFileUpload"
              />
            </label>
            <p
              v-if="formErrors.audience"
              class="min-w-0 mb-0 text-xs text-n-ruby-9"
            >
              {{ formErrors.audience }}
            </p>
          </div>
          <p class="mb-0 text-xs text-n-slate-11">
            {{
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.COUNT', {
                count: audienceCount,
              })
            }}
          </p>
        </div>

        <div
          class="flex items-start gap-3 rounded-lg border border-n-teal-7 bg-n-teal-3 p-3 text-sm text-n-teal-12"
        >
          <span class="i-lucide-shield-check mt-0.5 size-5 shrink-0" />
          <span>
            <span class="block font-medium">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PROTECTION_TITLE') }}
            </span>
            <span class="mt-1 block text-xs leading-5 text-n-teal-11">
              {{
                t(
                  'CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PROTECTION_DESCRIPTION'
                )
              }}
            </span>
          </span>
        </div>

        <label
          class="flex items-start gap-2 rounded-lg border border-n-weak bg-n-alpha-black2 p-3 text-sm text-n-slate-12"
        >
          <Checkbox
            v-model="state.openConversationOnReplyOnly"
            class="mt-0.5"
          />
          <span>
            <span class="block font-medium">{{ inboxBehaviorCopy.title }}</span>
            <span class="mt-1 block text-xs leading-5 text-n-slate-11">
              {{ inboxBehaviorCopy.description }}
            </span>
          </span>
        </label>

        <Input
          v-model="state.scheduledAt"
          type="datetime-local"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
          :min="currentDateTime"
          :placeholder="
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')
          "
          :message="formErrors.scheduledAt"
          :message-type="formErrors.scheduledAt ? 'error' : 'info'"
        />
      </div>
    </div>

    <div
      class="relative z-10 flex flex-shrink-0 items-center justify-between gap-3 border-t border-n-weak bg-n-solid-2 px-6 py-5 shadow-[0_-18px_34px_rgba(0,0,0,0.28)] before:pointer-events-none before:absolute before:inset-x-0 before:-top-8 before:h-8 before:bg-gradient-to-t before:from-n-solid-2 before:to-transparent"
    >
      <Button
        variant="outline"
        color="slate"
        type="button"
        :label="
          currentStep === 1
            ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')
            : t('CAMPAIGN.WHATSAPP.CREATE.FORM.WIZARD.BACK')
        "
        class="w-full"
        @click="handlePrevious"
      />
      <Button
        :label="
          isLastStep
            ? isEditing
              ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.WIZARD.SAVE')
              : t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')
            : t('CAMPAIGN.WHATSAPP.CREATE.FORM.WIZARD.NEXT')
        "
        class="w-full"
        :type="isLastStep ? 'submit' : 'button'"
        :is-loading="isLastStep && isSubmitting"
        :disabled="isLastStep && (isSubmitting || isSubmitDisabled)"
        @click="!isLastStep && handleNext()"
      />
    </div>
  </form>
</template>
